import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import moment from "moment";
import { useEffect, useState } from "react";
import LoadingOverlay from "react-loading-overlay-ts";
import PullToRefresh from "react-simple-pull-to-refresh";
import PageHeader from "../components/PageHeader";
import useAuth from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { GoogleSheetsService, Show } from '../services/GoogleSheetsService';


interface ShowListItem {
  showId: string;
  showName: string;
  showDateTime: string;
}

const ShowResultsPage: React.FC = () => {
  const user = useAuth();
  const navigate = useNavigate();
  const [showList, setShowList] = useState<ShowListItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = `Show Results`;
  }, []);

  const fetchShowList = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Always use public read-only access for show results list
      const service = new GoogleSheetsService();
      // Explicitly fetch all show results from Show Results sheet
      const allResults = await service.getAllShowResults();
      console.debug('Fetched show results:', allResults);
      // Fetch all shows for cross-reference
      const allShows = await service.getAllShows();
      console.debug('Fetched shows:', allShows);
      // Group results by showId
      const uniqueShowIds = Array.from(new Set(allResults.map(r => r.showId).filter(Boolean)));
      // Map showId to showName/date if available
      const showList: ShowListItem[] = uniqueShowIds.map(showId => {
        const show = allShows.find(s => s.showId === showId);
        return {
          showId,
          showName: show?.showName || showId,
          showDateTime: show?.showDateTime || '',
        };
      });
      // Sort by date descending if possible
      showList.sort((a, b) => (b.showDateTime || '').localeCompare(a.showDateTime || ''));
      setShowList(showList);
    } catch (err) {
      setError('Failed to load show list: ' + (err instanceof Error ? err.message : String(err)));
      // Log error for debugging
      // eslint-disable-next-line no-console
      console.error('ShowResultsPage fetchShowList error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchShowList();
    // eslint-disable-next-line
  }, [user]);

  const goToShowResult = (showId: string) => {
    navigate(`/showResults/${encodeURIComponent(showId)}`);
  };

  return (
    <LoadingOverlay active={isLoading} className="loading-overlay">
      <PullToRefresh onRefresh={fetchShowList}>
        <>
          <Container
            maxWidth="sm"
            sx={{ marginBottom: "60px" }}
            className="page-header"
          >
            <PageHeader header="Show Results" />
            {showList.length === 0 && !isLoading && (
              <div className="center-text" key="no-content">
                <h2>No shows with results</h2>
              </div>
            )}
            <Stack spacing={2}>
              {showList.map((show) => (
                <div
                  key={show.showId}
                  className="show-results-list-row card-row pointer"
                  onClick={() => goToShowResult(show.showId)}
                >
                  <div className="show-results-list-title">
                    {show.showName}
                  </div>
                  <div className="show-results-list-date">
                    {show.showDateTime ? moment(show.showDateTime).format("MM/DD/YYYY") : ''}
                  </div>
                </div>
              ))}
            </Stack>
            {error && <div style={{ color: 'red' }}>{error}</div>}
          </Container>
        </>
      </PullToRefresh>
    </LoadingOverlay>
  );
};

export default ShowResultsPage;
